import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Users,
  Edit2,
  Trash2,
  UserPlus,
  X,
  ChevronRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
} from 'lucide-react';
import { useEvent, useEventAttendees } from '../hooks/useEvents';
import {
  useUpdateEvent,
  useDeleteEvent,
  useUpdateEventStatus,
  useAddAttendee,
  useUpdateAttendeeStatus,
  useRemoveAttendee,
} from '../hooks/useEventMutations';
import { EventForm } from './EventForm';
import type {
  Event,
  EventFormValues,
  EventAttendee,
  AttendeeStatus,
} from '../types';
import {
  EVENT_TYPE_LABELS,
  EVENT_STATUS_COLORS,
  EVENT_STATUS_LABELS,
  EVENT_COLOR_MAP,
  ATTENDEE_STATUS_COLORS,
  ATTENDEE_STATUS_LABELS,
} from '../types';
import { supabase } from '@/lib/supabase';
import { useQuery } from '@tanstack/react-query';

// ── Contact search hook ────────────────────────────────────────────────────────

function useContactSearch(query: string) {
  return useQuery({
    queryKey: ['contacts', 'search', query],
    queryFn: async () => {
      if (query.trim().length < 2) return [];
      const { data, error } = await (supabase as any)
        .from('contacts')
        .select('id, full_name, phone, category')
        .ilike('full_name', `%${query.trim()}%`)
        .eq('active', true)
        .limit(8);
      if (error) throw error;
      return data ?? [];
    },
    enabled: query.trim().length >= 2,
    staleTime: 30_000,
  });
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function EventDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: event, isLoading, error } = useEvent(id);
  const { data: attendees = [] } = useEventAttendees(id);

  const updateEvent = useUpdateEvent();
  const deleteEvent = useDeleteEvent();
  const updateStatus = useUpdateEventStatus();

  const [showEdit, setShowEdit]     = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showAddAttendee, setShowAddAttendee] = useState(false);

  // ── Edit ───────────────────────────────────────────────────────────────────

  async function handleEdit(values: EventFormValues) {
    await updateEvent.mutateAsync({ id: id!, values });
    setShowEdit(false);
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  async function handleDelete() {
    await deleteEvent.mutateAsync(id!);
    navigate('/app/eventos');
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (isLoading) return <DetailSkeleton />;

  if (error || !event) {
    return (
      <div className="flex flex-col items-center gap-4 py-20 text-center">
        <AlertCircle className="h-8 w-8 text-danger" />
        <p className="text-sm text-ink-muted">Evento não encontrado.</p>
        <Link to="/app/eventos" className="text-xs font-semibold text-accent hover:underline">
          ← Voltar à agenda
        </Link>
      </div>
    );
  }

  const colorCfg   = EVENT_COLOR_MAP[event.color];
  const startDate  = new Date(event.starts_at);
  const endDate    = event.ends_at ? new Date(event.ends_at) : null;

  const dateLabel = startDate.toLocaleDateString('pt-BR', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const timeLabel = startDate.toLocaleTimeString('pt-BR', {
    hour: '2-digit', minute: '2-digit',
  });
  const endTimeLabel = endDate
    ? endDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : null;

  const confirmedCount = attendees.filter((a) =>
    a.status === 'confirmed' || a.status === 'attended',
  ).length;

  return (
    <div className="space-y-8 pb-12 animate-fade-in">

      {/* ── Breadcrumb + actions ── */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-sm">
          <Link
            to="/app/eventos"
            className="flex items-center gap-1.5 text-ink-muted hover:text-ink transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Agenda</span>
          </Link>
          <ChevronRight className="h-3 w-3 text-ink-faint" />
          <span className="text-ink font-medium truncate max-w-[200px]">{event.title}</span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEdit(true)}
            className="flex items-center gap-1.5 rounded-xl btn-ghost px-3.5 py-2 text-xs font-semibold"
          >
            <Edit2 className="h-3.5 w-3.5" />
            <span>Editar</span>
          </button>
          {event.status === 'scheduled' && (
            <button
              onClick={() => setShowDelete(true)}
              className="flex items-center gap-1.5 rounded-xl bg-danger/10 text-danger hover:bg-danger/20 px-3.5 py-2 text-xs font-semibold transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>Excluir</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Hero ── */}
      <div className={`relative overflow-hidden rounded-2xl card-premium border-l-4 ${colorCfg.card} p-6 md:p-8`}>
        <div className="flex items-start gap-4 flex-wrap justify-between">
          <div className="space-y-2 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="rounded-full bg-midnight-4 px-3 py-1 text-xs font-semibold text-ink-muted">
                {EVENT_TYPE_LABELS[event.type]}
              </span>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${EVENT_STATUS_COLORS[event.status]}`}>
                {EVENT_STATUS_LABELS[event.status]}
              </span>
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-extrabold text-ink tracking-tight">
              {event.title}
            </h1>
            {event.description && (
              <p className="text-sm text-ink-muted leading-relaxed max-w-2xl">
                {event.description}
              </p>
            )}
          </div>

          {/* Attendee count chip */}
          <div className="flex flex-col items-center justify-center rounded-xl bg-midnight-4 px-5 py-4 min-w-[80px]">
            <span className="font-display text-3xl font-extrabold text-ink">{attendees.length}</span>
            <span className="text-[10px] text-ink-muted uppercase tracking-widest mt-0.5">Convidados</span>
          </div>
        </div>

        {/* Metadata row */}
        <div className="mt-6 flex flex-wrap gap-5">
          <MetaItem icon={Calendar} label="Data">
            <span className="capitalize">{dateLabel}</span>
          </MetaItem>
          <MetaItem icon={Clock} label="Horário">
            {timeLabel}{endTimeLabel && ` – ${endTimeLabel}`}
          </MetaItem>
          {event.location_name && (
            <MetaItem icon={MapPin} label="Local">
              <span>{event.location_name}</span>
              {event.location_address && (
                <span className="text-ink-subtle block text-[11px]">{event.location_address}</span>
              )}
            </MetaItem>
          )}
          <MetaItem icon={Users} label="Confirmados">
            {confirmedCount} de {attendees.length}
          </MetaItem>
        </div>
      </div>

      {/* ── Status actions ── */}
      <StatusActions event={event} onUpdate={updateStatus.mutateAsync} />

      {/* ── Two-column body ── */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

        {/* Attendees — 2 cols */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-base font-bold text-ink">Participantes</h2>
            <button
              onClick={() => setShowAddAttendee(true)}
              className="flex items-center gap-1.5 rounded-xl btn-ghost px-3 py-1.5 text-xs font-semibold"
            >
              <UserPlus className="h-3.5 w-3.5" />
              <span>Adicionar</span>
            </button>
          </div>

          {attendees.length === 0 ? (
            <div className="rounded-2xl card-premium p-8 flex flex-col items-center gap-3 text-center">
              <div className="h-12 w-12 rounded-2xl bg-midnight-4 flex items-center justify-center">
                <Users className="h-5 w-5 text-ink-subtle" />
              </div>
              <p className="text-sm text-ink-muted">Nenhum participante convidado ainda.</p>
              <button
                onClick={() => setShowAddAttendee(true)}
                className="text-xs font-semibold text-accent hover:underline"
              >
                Adicionar participante →
              </button>
            </div>
          ) : (
            <div className="rounded-2xl card-premium divide-y divide-hairline overflow-hidden">
              {attendees.map((att) => (
                <AttendeeRow
                  key={att.id}
                  attendee={att}
                  eventId={id!}
                />
              ))}
            </div>
          )}
        </div>

        {/* Notes / meta sidebar */}
        <div className="space-y-4">
          {event.notes && (
            <div className="rounded-2xl card-premium p-5 space-y-3">
              <h3 className="font-mono text-[10px] font-semibold uppercase tracking-widest text-ink-subtle">
                Notas internas
              </h3>
              <p className="text-sm text-ink-muted whitespace-pre-wrap leading-relaxed">
                {event.notes}
              </p>
            </div>
          )}

          <div className="rounded-2xl card-premium p-5 space-y-3">
            <h3 className="font-mono text-[10px] font-semibold uppercase tracking-widest text-ink-subtle">
              Metadados
            </h3>
            <MetaRow label="Criado em">
              {new Date(event.created_at).toLocaleDateString('pt-BR', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </MetaRow>
            <MetaRow label="Última atualização">
              {new Date(event.updated_at).toLocaleDateString('pt-BR', {
                day: 'numeric', month: 'short', year: 'numeric',
              })}
            </MetaRow>
            <MetaRow label="ID">
              <span className="font-mono text-[10px] text-ink-faint break-all">{event.id}</span>
            </MetaRow>
          </div>
        </div>
      </div>

      {/* ── Edit modal ── */}
      {showEdit && (
        <Modal title="Editar evento" onClose={() => setShowEdit(false)}>
          <EventForm
            initial={event}
            onSubmit={handleEdit}
            onCancel={() => setShowEdit(false)}
            isSubmitting={updateEvent.isPending}
          />
        </Modal>
      )}

      {/* ── Delete confirmation ── */}
      {showDelete && (
        <Modal title="Excluir evento" onClose={() => setShowDelete(false)}>
          <div className="space-y-5">
            <p className="text-sm text-ink-muted">
              Tem certeza que deseja excluir o evento{' '}
              <span className="font-semibold text-ink">"{event.title}"</span>?
              Esta ação não pode ser desfeita. Todos os participantes serão removidos.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDelete(false)}
                className="rounded-xl btn-ghost px-4 py-2.5 text-sm font-semibold"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteEvent.isPending}
                className="rounded-xl bg-danger text-white hover:bg-danger/80 px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60"
              >
                {deleteEvent.isPending ? 'Excluindo…' : 'Excluir evento'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Add attendee modal ── */}
      {showAddAttendee && (
        <AddAttendeeModal
          eventId={id!}
          existingIds={attendees.map((a) => a.contact_id)}
          onClose={() => setShowAddAttendee(false)}
        />
      )}
    </div>
  );
}

// ── Status actions ─────────────────────────────────────────────────────────────

interface StatusActionsProps {
  event: Event;
  onUpdate: (args: { id: string; status: Event['status'] }) => Promise<unknown>;
}

function StatusActions({ event, onUpdate }: StatusActionsProps) {
  const [loading, setLoading] = useState(false);

  async function change(status: Event['status']) {
    setLoading(true);
    try { await onUpdate({ id: event.id, status }); }
    finally { setLoading(false); }
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {event.status === 'scheduled' && (
        <button
          disabled={loading}
          onClick={() => change('ongoing')}
          className="flex items-center gap-1.5 rounded-xl btn-accent px-4 py-2 text-sm font-semibold disabled:opacity-60"
        >
          <CheckCircle2 className="h-4 w-4" />
          Iniciar evento
        </button>
      )}
      {event.status === 'ongoing' && (
        <>
          <button
            disabled={loading}
            onClick={() => change('completed')}
            className="flex items-center gap-1.5 rounded-xl btn-accent px-4 py-2 text-sm font-semibold disabled:opacity-60"
          >
            <CheckCircle2 className="h-4 w-4" />
            Concluir
          </button>
          <button
            disabled={loading}
            onClick={() => change('cancelled')}
            className="flex items-center gap-1.5 rounded-xl bg-danger/10 text-danger hover:bg-danger/20 px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
          >
            <XCircle className="h-4 w-4" />
            Cancelar
          </button>
        </>
      )}
      {event.status === 'scheduled' && (
        <button
          disabled={loading}
          onClick={() => change('cancelled')}
          className="flex items-center gap-1.5 rounded-xl bg-danger/10 text-danger hover:bg-danger/20 px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-60"
        >
          <XCircle className="h-4 w-4" />
          Cancelar evento
        </button>
      )}
    </div>
  );
}

// ── Attendee row ──────────────────────────────────────────────────────────────

function AttendeeRow({ attendee, eventId }: { attendee: EventAttendee; eventId: string }) {
  const updateStatus = useUpdateAttendeeStatus();
  const removeAttendee = useRemoveAttendee();
  const [menuOpen, setMenuOpen] = useState(false);

  const statusOptions: AttendeeStatus[] = ['invited', 'confirmed', 'declined', 'attended', 'no_show'];

  return (
    <div className="group flex items-center gap-3 px-4 py-3 hover:bg-white/[0.03] transition-colors">
      {/* Avatar */}
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent text-sm font-bold">
        {(attendee.contact?.full_name ?? '?')[0].toUpperCase()}
      </div>

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ink truncate">
          {attendee.contact?.full_name ?? 'Contato removido'}
        </p>
        {attendee.contact?.phone && (
          <p className="text-xs text-ink-subtle">{attendee.contact.phone}</p>
        )}
      </div>

      {/* Status badge + dropdown */}
      <div className="relative">
        <button
          onClick={() => setMenuOpen((o) => !o)}
          className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold cursor-pointer transition-opacity hover:opacity-80 ${ATTENDEE_STATUS_COLORS[attendee.status]}`}
        >
          {ATTENDEE_STATUS_LABELS[attendee.status]}
        </button>

        {menuOpen && (
          <>
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 z-20 mt-1 w-40 rounded-xl card-premium shadow-deep py-1 overflow-hidden">
              {statusOptions.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    updateStatus.mutate({ id: attendee.id, event_id: eventId, status: s });
                    setMenuOpen(false);
                  }}
                  className={`w-full px-3 py-2 text-left text-xs font-medium hover:bg-white/[0.06] transition-colors ${
                    s === attendee.status ? 'text-accent' : 'text-ink-muted'
                  }`}
                >
                  {ATTENDEE_STATUS_LABELS[s]}
                </button>
              ))}
              <div className="border-t border-hairline mt-1 pt-1">
                <button
                  onClick={() => {
                    removeAttendee.mutate({ id: attendee.id, event_id: eventId });
                    setMenuOpen(false);
                  }}
                  className="w-full px-3 py-2 text-left text-xs font-medium text-danger hover:bg-danger/10 transition-colors"
                >
                  Remover
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Add attendee modal ────────────────────────────────────────────────────────

interface AddAttendeeModalProps {
  eventId: string;
  existingIds: string[];
  onClose: () => void;
}

function AddAttendeeModal({ eventId, existingIds, onClose }: AddAttendeeModalProps) {
  const [search, setSearch] = useState('');
  const addAttendee = useAddAttendee();
  const { data: results = [] } = useContactSearch(search);

  async function handleAdd(contactId: string) {
    await addAttendee.mutateAsync({ event_id: eventId, contact_id: contactId });
  }

  return (
    <Modal title="Adicionar participante" onClose={onClose}>
      <div className="space-y-4">
        <input
          autoFocus
          type="search"
          placeholder="Buscar contato pelo nome…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-xl bg-midnight-2 border border-hairline px-4 py-2.5 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 transition-colors"
        />

        {search.trim().length >= 2 && results.length === 0 && (
          <p className="text-xs text-ink-muted text-center py-4">Nenhum contato encontrado.</p>
        )}

        <div className="space-y-1 max-h-72 overflow-y-auto">
          {results.map((c: { id: string; full_name: string; phone: string | null; category: string | null }) => {
            const already = existingIds.includes(c.id);
            return (
              <button
                key={c.id}
                disabled={already || addAttendee.isPending}
                onClick={() => handleAdd(c.id)}
                className="group w-full flex items-center gap-3 rounded-xl px-3 py-2.5 hover:bg-white/[0.06] disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-left"
              >
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent text-sm font-bold">
                  {c.full_name[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-ink">{c.full_name}</p>
                  {c.phone && <p className="text-xs text-ink-subtle">{c.phone}</p>}
                </div>
                {already ? (
                  <span className="text-[10px] text-ink-faint">Já convidado</span>
                ) : (
                  <span className="text-xs font-semibold text-accent opacity-0 group-hover:opacity-100 transition-opacity">
                    Adicionar
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

// ── Shared helpers ─────────────────────────────────────────────────────────────

function MetaItem({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 text-ink-subtle mt-0.5 shrink-0" />
      <div>
        <p className="text-[10px] text-ink-faint uppercase tracking-widest font-semibold">{label}</p>
        <div className="text-sm text-ink mt-0.5">{children}</div>
      </div>
    </div>
  );
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-2 text-xs">
      <span className="text-ink-subtle">{label}</span>
      <span className="text-ink text-right">{children}</span>
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-2xl card-premium shadow-deep overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between p-6 border-b border-hairline">
          <h2 className="font-display text-lg font-bold text-ink">{title}</h2>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-ink-subtle hover:text-ink hover:bg-white/[0.06] transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-8 animate-pulse pb-12">
      <div className="flex justify-between">
        <div className="h-5 w-48 rounded bg-midnight-4" />
        <div className="flex gap-2">
          <div className="h-9 w-24 rounded-xl bg-midnight-4" />
          <div className="h-9 w-24 rounded-xl bg-midnight-4" />
        </div>
      </div>
      <div className="rounded-2xl bg-midnight-3 h-48" />
      <div className="grid grid-cols-3 gap-6">
        <div className="col-span-2 rounded-2xl bg-midnight-3 h-64" />
        <div className="rounded-2xl bg-midnight-3 h-64" />
      </div>
    </div>
  );
}
